import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { MAIN_ID } from "./main-region.ts";
import {
  currentPath,
  dismissSessionEnded,
  isSigningOut,
  sessionEndedAt,
  signInHref,
  signInNavigation,
  useHasUnsavedWork,
  useSessionEnded,
  useSessionEndedDismissed,
  useSessionEndReason,
  useUnsavedText,
} from "./session-ended.ts";

/**
 * Shown on every screen once the session has ended. With nothing to lose it goes to the
 * sign-in page by itself (which comes back to this page). With a message being typed or an
 * upload or edit in progress it never leaves by itself, whatever the reason (expired, signed
 * out elsewhere, revoked): the banner stays until the person signs in again, copies their
 * unsaved text or dismisses it.
 */
export function SessionEnded() {
  const { t } = useTranslation();
  const ended = useSessionEnded();
  const reason = useSessionEndReason();
  const unsaved = useHasUnsavedWork();
  const dismissed = useSessionEndedDismissed();
  const text = useUnsavedText();
  // The last copy attempt, for this ended session (`at`) and this exact text.
  const [copy, setCopy] = useState<{ ok: boolean; text: string; at: number } | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `reason` is read when it leaves
  useEffect(() => {
    if (ended && !unsaved && !isSigningOut()) {
      signInNavigation.go(signInHref(currentPath(), reason));
    }
  }, [ended, unsaved]);

  if (!ended || dismissed) {
    return null;
  }
  const at = sessionEndedAt();
  // About the text on screen only: after an edit, neither "Copied" nor "Couldn't copy" holds.
  const current = copy && copy.at === at && copy.text === text ? copy : null;
  const outcome = current ? (current.ok ? "copied" : "failed") : null;
  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopy({ ok: true, text, at });
    } catch {
      // No clipboard (an insecure context, a blurred document, a refused permission).
      setCopy({ ok: false, text, at });
    }
  };
  const dismiss = () => {
    dismissSessionEnded();
    // The focused button is gone: continue from the page content, not the document start.
    document.getElementById(MAIN_ID)?.focus();
  };
  return (
    <div className="shrink-0 border-b border-line px-4 py-2 sm:px-6">
      <Notice
        tone="warning"
        live
        action={
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm">
              <a href={signInHref(currentPath(), reason)}>{t("session.signInAgain")}</a>
            </Button>
            {text !== "" ? (
              <Button size="sm" variant="outline" onClick={copyText}>
                {t("session.copyUnsaved")}
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={dismiss}>
              {t("session.dismiss")}
            </Button>
          </div>
        }
      >
        {t(`session.ended.${reason}`)} {unsaved ? t("session.unsaved") : null}
        {outcome ? (
          <span className="block font-medium">
            {outcome === "copied" ? t("session.copiedOk") : t("session.copyFailed")}
          </span>
        ) : null}
      </Notice>
    </div>
  );
}
